import PropTypes from 'prop-types';
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useAtom } from 'jotai';
// @mui
import { styled, useTheme } from '@mui/material/styles';
import { Box, Stack, Drawer } from '@mui/material';
// utils
import cssStyles from '../../../../utils/cssStyles';
// config
import { NAVBAR } from '../../../config';
import { PATH_DASHBOARD } from '../../../../shared/routes/paths';
// hooks
import { useResponsive, useCollapseDrawer } from '../../../../shared/hooks';
// components
import { Scrollbar, Iconify, Logo } from '../../../../shared/components/base';
import { NavSectionVertical } from '../../../../shared/components/nav-section';
import { userRecordAtom } from '../../../../shared/state/atoms';

// ----------------------------------------------------------------------

const RootStyle = styled('div')(({ theme }) => ({
  [theme.breakpoints.up('lg')]: {
    flexShrink: 0,
    transition: theme.transitions.create('width', {
      duration: theme.transitions.duration.shorter,
    }),
  },
}));

// ----------------------------------------------------------------------

NavbarVertical.propTypes = {
  isOpenSidebar: PropTypes.bool,
  onCloseSidebar: PropTypes.func,
};

export default function NavbarVertical({ isOpenSidebar, onCloseSidebar }) {
  const theme = useTheme();

  const { pathname } = useLocation();

  const isDesktop = useResponsive('up', 'lg');

  const { isCollapse, collapseClick, collapseHover, onHoverEnter, onHoverLeave } = useCollapseDrawer();

  // dynamic nav based on supplier type stored on userRecord
  const [userRecord] = useAtom(userRecordAtom);
  const baseItems = [{ title: 'Dashboard', path: PATH_DASHBOARD.root, icon: <Iconify icon={'ph:house-simple'} /> }];
  let listingItem = null;
  if (userRecord?.type === 'Restaurant') {
    listingItem = { title: 'Restaurant Listings', path: PATH_DASHBOARD.general.restaurantListings, icon: <Iconify icon={'ph:list'} /> };
  } else if (userRecord?.type === 'Activity') {
    listingItem = { title: 'Tour Listings', path: PATH_DASHBOARD.general.tourListings, icon: <Iconify icon={'ph:list'} /> };
  }
  const accountItem = { title: 'Account Settings', path: PATH_DASHBOARD.general.account, icon: <Iconify icon={'ph:user'} /> };
  const dynamicNav = [
    { subheader: 'Overview', items: baseItems },
    { subheader: 'Management', items: listingItem ? [listingItem, accountItem] : [accountItem] },
  ];

  useEffect(() => {
    if (isOpenSidebar) {
      onCloseSidebar();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  const renderContent = (
    <Scrollbar
      sx={{
        height: 1,
        '& .simplebar-content': { height: 1, display: 'flex', flexDirection: 'column' },
      }}
    >
      <Stack
        spacing={2}
        sx={{
          pt: 2.5,
          pb: 4.5,
          px: 2.5,
          flexShrink: 0,
          ...(isCollapse && { alignItems: 'center' }),
        }}
      >
        <Stack direction="row" alignItems="center" justifyContent="flex-start" sx={{ px: 0.5 }}>
          <Logo disabledLink sx={{ width: isCollapse ? 40 : 140 }} />
        </Stack>
      </Stack>

      <NavSectionVertical navConfig={dynamicNav} isCollapse={isCollapse} />

      <Box sx={{ flexGrow: 1 }} />
    </Scrollbar>
  );

  return (
    <RootStyle
      sx={{
        width: {
          lg: isCollapse ? NAVBAR.DASHBOARD_COLLAPSE_WIDTH : NAVBAR.DASHBOARD_WIDTH,
        },
        ...(collapseClick && {
          position: 'absolute',
        }),
      }}
    >
      {!isDesktop && (
        <Drawer open={isOpenSidebar} onClose={onCloseSidebar} PaperProps={{ sx: { width: NAVBAR.DASHBOARD_WIDTH } }}>
          {renderContent}
        </Drawer>
      )}

      {isDesktop && (
        <Drawer
          open
          variant="persistent"
          onMouseEnter={onHoverEnter}
          onMouseLeave={onHoverLeave}
          PaperProps={{
            sx: {
              width: NAVBAR.DASHBOARD_WIDTH,
              borderRightStyle: 'dashed',
              bgcolor: 'background.default',
              transition: (theme) =>
                theme.transitions.create('width', {
                  duration: theme.transitions.duration.standard,
                }),
              ...(isCollapse && {
                width: NAVBAR.DASHBOARD_COLLAPSE_WIDTH,
              }),
              ...(collapseHover && {
                ...cssStyles(theme).bgBlur(),
                boxShadow: (theme) => theme.customShadows.z24,
              }),
            },
          }}
        >
          {renderContent}
        </Drawer>
      )}
    </RootStyle>
  );
}
